import React, { useState, useEffect, SetStateAction } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Tooltip,
} from "@mui/material";

import {
  Description as FileIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import { KnowledgeBase } from "../../../Shared-ui/Types/KnowledgeBase";
import { KNOWLEDGE_BASE, SHAREPOINT, TOOLS } from "../../../Services/Endpoint";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { useDispatch } from "react-redux";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { SharepointFile } from "../../../Shared-ui/Types/common";

interface SharePointToolkitConfig {
  toolkit_type: string;
  client_id?: string;
  tenant_id?: string;
  x5t_value?: string;
  private_key?: string;
  scope?: string;
}

interface ToolkitItem {
  id: string;
  name: string;
  description?: string;
  type: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  config: SharePointToolkitConfig;
}

interface SharePointFile {
  name: string;
  server_relative_url: string;
}

interface PaginationMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_page: number | null;
  prev_page: number | null;
}

interface SharePointTabProps {
  knowledgeBase: KnowledgeBase;
  setKnowledgeBase: React.Dispatch<SetStateAction<KnowledgeBase>>;
  setKnowledgeBaseOfSharePoint: React.Dispatch<SetStateAction<any>>;
  // Add this new prop
  onFileRetrieval: (toolId: string, kbId: string) => Promise<void>;
}

const SharePointTab: React.FC<SharePointTabProps> = (props) => {
  const { setKnowledgeBaseOfSharePoint } = props;
  const dispatch = useDispatch();

  const [tools, setTools] = useState<ToolkitItem[]>([]);
  console.log(tools, 'tools');


  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  const [selectedToolkit, setSelectedToolkit] = useState<string>("");
  console.log(selectedToolkit, 'selectedToolkit');

  const [site, setSite] = useState<string>("");
  const [folder, setFolder] = useState<string>("");
  const [files, setFiles] = useState<SharePointFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [sharepointSelectedFiles, setSharepointSelectedFiles] = useState<SharepointFile[]>([] as SharepointFile[]);
  const [selectedShareFileSync, setSelectedShareFileSync] = useState<string[]>([]);

  const fetchSharePointTool = async () => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(TOOLS, {
        params: {
          page: page,
          page_size: pageSize,
          toolkit_type: 'sharepoint',
        },
      }).then((res) => res?.data);

      setTools(data?.items || []);
      setPagination(data?.pagination || null);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText || err?.message || 'Failed to fetch SharePoint toolkits'));
    } finally {
      dispatch(StopLoading());
    }
  };

  const fetchFiles = async () => {
    if (!selectedToolkit) return;

    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${SHAREPOINT}/${selectedToolkit}/files`, {
        params: {
          site: site,
        },
      }).then((res) => res?.data);

      if (data?.status === 'success') {
        setFiles(data.files || []);
        setSelectedFiles([]); // Clear previous selections
      } else {
        dispatch(ShowError('Failed to fetch files'));
      }
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText || err?.message || 'Failed to fetch SharePoint files'));
    } finally {
      dispatch(StopLoading());
    }
  };

  const fetchSharepointAddedFiles = async () => {
    if (!props.knowledgeBase?.id) return;
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.get(`${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/sharepoint-files`).then((res) => res?.data);
      setSharepointSelectedFiles(data || []);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText || err?.message || 'Failed to fetch SharePoint files'));
    } finally {
      dispatch(StopLoading());
    }
  };

  const syncSharepointFiles = async () => {
    if (!props.knowledgeBase?.id) return;
    try {
      dispatch(StartLoading());
      const queryString = selectedShareFileSync.map(id => `file_ids=${encodeURIComponent(id)}`).join('&');
      const finalUrl = `${KNOWLEDGE_BASE}/${props.knowledgeBase.id}/sharepoint-files?${queryString}`;
      const data = await BaseInstance.put(finalUrl).then((res) => res?.data);
      dispatch(ShowSuccess(data.message))
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText || err?.message || 'Failed to fetch SharePoint files'));
    } finally {
      dispatch(StopLoading());
    }
  };


  useEffect(() => {
    fetchSharePointTool();
    fetchSharepointAddedFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handlePrev = () => {
    if (pagination?.has_prev && pagination.prev_page) {
      setPage(pagination.prev_page);
    }
  };

  const handleNext = () => {
    if (pagination?.has_next && pagination.next_page) {
      setPage(pagination.next_page);
    }
  };

  const handleToolkitChange = (event: any) => {
    const toolkitId = event.target.value;
    setSelectedToolkit(toolkitId);
    setFiles([]);
    setFilesError(null);
    // Directly update tool_id in config using parent setter
    props.setKnowledgeBase((prev: KnowledgeBase) => ({
      ...prev,
      config: {
        ...prev.config,
        tool_id: toolkitId,
      },
    }));
  };

  const handleSubmit = () => {
    fetchFiles();
  };

  const handleFileSelect = (fileName: string) => {
    setSelectedFiles(prev =>
      prev.includes(fileName)
        ? prev.filter(name => name !== fileName)
        : [...prev, fileName]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(file => file.name));
    }
  };

  const handleFileSelectSyncAll = () => {
    if (selectedShareFileSync.length === sharepointSelectedFiles.length) {
      setSelectedShareFileSync([]);
    } else {
      setSelectedShareFileSync(sharepointSelectedFiles.map(file => file.file_id));
    }
  };

  const handleFileSelectSync = (fileId: string) => {
    setSelectedShareFileSync(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  // Update knowledge base data when selected files change
  useEffect(() => {
    if (selectedToolkit && site && files.length > 0) {
      const selectedFileData = files.filter(file => selectedFiles.includes(file.name));
      setKnowledgeBaseOfSharePoint({
        files: selectedFileData,
        toolkit_id: selectedToolkit,
        site: site,
      });
    }
  }, [selectedFiles, selectedToolkit, site, files, setKnowledgeBaseOfSharePoint]);

  const truncateChars = (text: string | undefined, maxLength: number): string => {
    if (!text) return 'No description';
    if (text.length <= maxLength) {
      return text;
    }
    return text.slice(0, maxLength) + '...';
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="subtitle1" gutterBottom>
        SharePoint Toolkits
      </Typography>

      {/* Files List */}
      {sharepointSelectedFiles.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {sharepointSelectedFiles.length} files selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleFileSelectSyncAll}
                sx={{ minWidth: 'auto', px: 1 }}
              >

                {selectedShareFileSync.length === sharepointSelectedFiles.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button
                size="small"
                disabled={selectedShareFileSync.length == 0}
                variant="contained"
                onClick={syncSharepointFiles}
                sx={{ minWidth: 'auto', px: 1 }}
              >

                Sync Files
              </Button>
            </Box>
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto', display: 'grid', gap: 1 }}>
            {sharepointSelectedFiles.map((file: SharepointFile, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Checkbox
                  checked={selectedShareFileSync.includes(file.file_id)}
                  onChange={() => handleFileSelectSync(file.file_id)}
                  size="small"
                />
                <FolderIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {file.file_name}
                  </Typography>
                  {/* <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {file.server_relative_url}
                  </Typography> */}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}


      {/* Toolkit Dropdown */}
      {tools.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="toolkit-select-label">Select Toolkit</InputLabel>
            <Select
              labelId="toolkit-select-label"
              id="toolkit-select"
              value={selectedToolkit}
              label="Select Toolkit"
              onChange={handleToolkitChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    // Constrain width to be at most the width of the dialog
                    maxWidth: '552px',
                    maxHeight: 300, // Add max height to make the list scrollable
                  },
                },
              }}
            >
              {tools.map((tool) => (
                <MenuItem key={tool.id} value={tool.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileIcon fontSize="small" />
                    <Box sx={{ minWidth: 0, whiteSpace: 'normal' }}>
                      <Typography variant="body2">{tool.name}</Typography>
                      <Tooltip title={tool.description || ''} arrow>
                        <Typography variant="caption" color="text.secondary">
                          {truncateChars(tool.description, 200)}
                          {' • '}
                          {tool.config?.toolkit_type || tool.type}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Site Input and Submit */}
      {selectedToolkit && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Site"
            placeholder="ZeaIQ"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!site}
            size="small"
          >
            Fetch Files
          </Button>
        </Box>
      )}

      {/* Files List */}
      {files.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {files.length} file(s) found • {selectedFiles.length} selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelectAll}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {selectedFiles.length === files.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto', display: 'grid', gap: 1 }}>
            {files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Checkbox
                  checked={selectedFiles.includes(file.name)}
                  onChange={() => handleFileSelect(file.name)}
                  size="small"
                />
                <FolderIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {file.server_relative_url}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* No Files Found */}
      {selectedToolkit && site && files.length === 0 && (
        <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography>No files found for the selected site</Typography>
        </Box>
      )}

      {/* No Tools Found */}
      {tools.length === 0 && (
        <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography>No toolkits found</Typography>
        </Box>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
          <Button variant="outlined" size="small" onClick={handlePrev} disabled={!pagination.has_prev}>
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.total_pages}
          </Typography>
          <Button variant="outlined" size="small" onClick={handleNext} disabled={!pagination.has_next}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SharePointTab; 